import React, { useState } from "react";
import "../styles/createMedication.css"; // Import global styles
import { SERVER_URL } from "../config";

const CreateMedication: React.FC = () => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    dosageForm: "",
    strength: "",
    price: "",
    type: "",
    imageUrl: "",
  });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [message, setMessage] = useState("");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = e.target;

    if (type === "file" && e.target.files) {
      setImageFile(e.target.files[0]); // Store image file
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const uploadImageAndGetUrl = async (file: File): Promise<string | null> => {
    try {
      // Step 1: Request a signed upload URL
      const response = await fetch(
        `${SERVER_URL}/api/files/generate-upload-url?fileName=${encodeURIComponent(
          file.name
        )}&fileType=${encodeURIComponent(file.type)}`
      );
      const data = await response.json();
      if (!response.ok)
        throw new Error(data.error || "Failed to get upload URL");

      // Step 2: Upload the file to the signed URL
      const uploadResponse = await fetch(data.uploadUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type },
      });
      if (!uploadResponse.ok) throw new Error("Failed to upload image");

      // Step 3: Get a public view URL for the uploaded file
      const viewResponse = await fetch(
        `${SERVER_URL}/api/files/generate-view-url?fileName=${encodeURIComponent(
          file.name
        )}`
      );
      const viewData = await viewResponse.json();
      if (!viewResponse.ok) throw new Error("Failed to get view URL");

      return viewData.viewUrl; // Return the image's public view URL
    } catch (error) {
      console.error("Image upload error:", error);
      setMessage("Image upload failed.");
      return null;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage("Uploading...");

    let imageUrl = formData.imageUrl; // Default to existing URL input

    if (imageFile && imageUrl !== "") {
      const uploadedUrl = await uploadImageAndGetUrl(imageFile);
      if (!uploadedUrl) {
        setMessage("Image upload failed. Try again.");
        return;
      }
      imageUrl = uploadedUrl; // Use the uploaded image's view URL
    }

    try {
      const response = await fetch(
        `${SERVER_URL}/api/medications/medications`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({
            ...formData,
            price: parseFloat(formData.price) || 0.0,
            imageUrl, // Use the uploaded image URL
          }),
        }
      );

      const data = await response.json();
      if (response.ok) {
        setMessage("Medication created successfully!");
        setFormData({
          name: "",
          description: "",
          dosageForm: "",
          strength: "",
          price: "",
          type: "",
          imageUrl: "",
        });
        setImageFile(null);
      } else {
        setMessage(data.error || "Failed to create medication.");
      }
    } catch (error) {
      setMessage("An error occurred while creating the medication.");
    }
  };

  return (
    <div className="medication-container">
      <h2>Create Medication</h2>
      {message && <p className="message">{message}</p>}
      <form onSubmit={handleSubmit} className="medication-form">
        <label>Medication Name</label>
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
        />

        <label>Type</label>
        <input
          placeholder="Enter the medication type (e.g., weight-loss, diabetes)"
          type="text"
          name="type"
          value={formData.type}
          onChange={handleChange}
        />

        <label>Description</label>
        <input
          type="text"
          name="description"
          value={formData.description}
          onChange={handleChange}
        />

        <label>Dosage Form</label>
        <input
          type="text"
          name="dosageForm"
          value={formData.dosageForm}
          onChange={handleChange}
        />

        <label>Strength</label>
        <input
          type="text"
          name="strength"
          value={formData.strength}
          onChange={handleChange}
        />

        <label>Price ($)</label>
        <input
          type="number"
          name="price"
          value={formData.price}
          onChange={handleChange}
          required
        />

        <label>Upload Image</label>
        <input
          type="file"
          name="imageFile"
          accept="image/*"
          onChange={handleChange}
        />

        <button type="submit">Create Medication</button>
      </form>
    </div>
  );
};

export default CreateMedication;
